import { useState } from "react"
import { ChevronDown, Loader2, Trash2 } from "lucide-react"

import { CreditCardIcon, SbpIcon } from "@/components/icons"
import { usePaymentMutations, usePaymentOptions } from "@/hooks/payments/usePayments"
import { cn } from "@/lib/utils"
import type { PaymentMethod } from "@/types/payment"

function formatCardMethodTitle(title: string): string {
  const last4 = title.replace(/\D/g, "").slice(-4)

  if (last4.length === 4) {
    return `•••• ${last4}`
  }

  return title
}

function SavedPaymentMethodItem({ method }: { method: PaymentMethod }) {
  const { deleteVaultMethod, setDefaultVaultMethod } = usePaymentMutations()
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const isPending = deleteVaultMethod.isPending || setDefaultVaultMethod.isPending
  const label =
    method.type === "qr"
      ? method.title
      : [method.paymentMethodName, formatCardMethodTitle(method.title)].filter(Boolean).join(" ")

  const handleDelete = () => {
    deleteVaultMethod.mutate(method.id, { onError: () => setIsConfirmingDelete(false) })
  }

  return (
    <div role="group" aria-label={label} className="rounded-lg bg-background sm:px-4 px-3 sm:py-3 py-2">
      <div className="flex min-w-0 items-center justify-between gap-3">
        {method.type === "qr" ? (
          <>
            <span className="truncate text-base leading-[22px] text-foreground">{method.title}</span>
            <SbpIcon size={20} />
          </>
        ) : (
          <>
            <span className="truncate text-base leading-[22px] text-foreground">
              {method.paymentMethodName ? `${method.paymentMethodName} ` : null}
              <span className="tracking-[0.08em]">{formatCardMethodTitle(method.title)}</span>
            </span>
            <CreditCardIcon size={20} />
          </>
        )}
      </div>

      <div className="mt-1 flex min-h-6 items-center justify-between gap-3 text-[14px] leading-[18px]">
        {isPending ? (
          <Loader2 className="size-4 animate-spin text-brand" aria-label="Сохраняем" />
        ) : isConfirmingDelete ? (
          <>
            <span className="text-foreground">Удалить способ оплаты?</span>
            <span className="flex shrink-0 items-center gap-4">
              <button type="button" className="cursor-pointer font-semibold text-foreground hover:underline" onClick={handleDelete}>
                Удалить
              </button>
              <button
                type="button"
                className="cursor-pointer text-muted-foreground hover:text-foreground"
                onClick={() => setIsConfirmingDelete(false)}
              >
                Отмена
              </button>
            </span>
          </>
        ) : (
          <>
            {method.isDefault ? (
              <span className="text-muted-foreground">Основной · для автоплатежа</span>
            ) : (
              <button
                type="button"
                className="cursor-pointer text-brand hover:underline"
                onClick={() => setDefaultVaultMethod.mutate(method.id)}
              >
                Сделать основным
              </button>
            )}
            <button
              type="button"
              className="-m-1 cursor-pointer rounded p-1 text-muted-foreground transition-colors hover:text-foreground"
              aria-label="Удалить способ оплаты"
              onClick={() => setIsConfirmingDelete(true)}
            >
              <Trash2 className="size-[18px]" aria-hidden />
            </button>
          </>
        )}
      </div>
    </div>
  )
}

type SavedPaymentMethodsDropdownProps = {
  enabled?: boolean
}

export function SavedPaymentMethodsDropdown({ enabled = true }: SavedPaymentMethodsDropdownProps) {
  const [open, setOpen] = useState(false)
  const { data, isLoading } = usePaymentOptions(enabled)
  const savedMethods = data?.savedMethods?.filter(method => method.status === "active") ?? []

  if (!enabled) {
    return null
  }

  if (!isLoading && savedMethods.length === 0) {
    return null
  }

  return (
    <div className="mb-4">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
        onClick={() => setOpen(prev => !prev)}
      >
        <span className="text-[16px] leading-[22px] text-foreground">Сохранённые способы оплаты</span>
        <span className="inline-flex items-center gap-2 text-muted-foreground">
          {!isLoading ? (
            <span className="text-[14px] leading-[18px]">{savedMethods.length}</span>
          ) : null}
          <ChevronDown
            className={cn("size-5 shrink-0 transition-transform", open && "rotate-180")}
            aria-hidden
          />
        </span>
      </button>

      {open ? (
        <div className="mt-3 flex flex-col sm:gap-2 gap-1">
          {isLoading ? (
            <div className="flex justify-center py-2">
              <Loader2 className="size-5 animate-spin text-brand" />
            </div>
          ) : (
            savedMethods.map(method => (
              <SavedPaymentMethodItem key={method.id} method={method} />
            ))
          )}
        </div>
      ) : null}
    </div>
  )
}
